import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Truck, ShieldCheck, Sparkles, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const DEFAULT_SLIDES = [
  {
    img: "/banner.png",
    eyebrow: "Smart Intimates · Made in India",
    title: ["Reveal your", "comfort"],
    sub: "Perfect Fit. Perfect Coverage. Perfectly Soft. Thoughtfully crafted camisoles & panties for women and kids.",
    to: "/shop",
  },
  {
    img: "/banner.png",
    eyebrow: "New Season",
    title: ["Softness", "redefined"],
    sub: "Discover our smart camisoles — breathable, seamless and made to move with you all day.",
    to: "/shop?category=Camisoles",
  },
  {
    img: "/banner.png",
    eyebrow: "Everyday Essentials",
    title: ["Comfort", "for all"],
    sub: "From women to kids — perfectly soft essentials in every size, every shade.",
    to: "/shop?category=Panties",
  },
];

const DEFAULT_TESTIMONIALS = [
  { quote: "Softest panties I've ever worn — the fit is just perfect and they last wash after wash.", author: "Priya, Mumbai" },
  { quote: "Bought camisoles for daily wear and they're so comfortable. Will order again for sure!", author: "Anjali, Delhi" },
  { quote: "Lovely quality and quick delivery on COD. My kids love their new comfy panties.", author: "Sneha, Pune" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [slide, setSlide] = useState(0);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/config").then((r) => {
      const b = r.data.banners;
      if (Array.isArray(b) && b.length) {
        setSlides(b.map((x) => ({ img: fileUrl(x.image), eyebrow: x.eyebrow, title: [x.title1, x.title2], sub: x.subtitle, to: x.link || "/shop" })));
        setSlide(0);
      }
      if (Array.isArray(r.data.testimonials)) setTestimonials(r.data.testimonials);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length]);

  const arrivals = products.slice(0, 8);
  const bestsellers = [...products].reverse().slice(0, 4);
  const go = (dir) => setSlide((s) => (s + dir + slides.length) % slides.length);

  return (
    <div>
      {/* HERO SLIDESHOW */}
      <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden" data-testid="hero-slideshow">
        {slides.map((sl, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? "opacity-100 z-10" : "opacity-0 z-0"}`}>
            <img src={sl.img} alt={sl.title.join(" ")} className={`absolute inset-0 w-full h-full object-cover ${i === slide ? "bc-kenburns" : ""}`} />
            <div className="absolute inset-0 bc-hero-overlay" />
            <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 h-full flex items-center">
              {i === slide && (
                <div className="max-w-xl text-white" key={slide}>
                  <p className="text-xs uppercase tracking-luxe mb-6 bc-fade-up" style={{ animationDelay: "100ms" }}>{sl.eyebrow}</p>
                  <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] font-light">
                    <span className="block bc-fade-up" style={{ animationDelay: "200ms" }}>{sl.title[0]}</span>
                    <span className="block bc-fade-up" style={{ animationDelay: "320ms" }}>{sl.title[1]}</span>
                  </h1>
                  <p className="text-base sm:text-lg font-light mt-6 max-w-md opacity-95 leading-relaxed bc-fade-up" style={{ animationDelay: "440ms" }}>{sl.sub}</p>
                  <div className="bc-fade-up" style={{ animationDelay: "560ms" }}>
                    <Link to={sl.to} className="inline-flex items-center gap-3 mt-9 bg-[hsl(var(--ivory))] text-foreground px-9 py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary hover:text-primary-foreground transition-colors duration-300" data-testid="hero-shop-btn">
                      Shop Collection <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {/* controls */}
        <button onClick={() => go(-1)} className="hidden sm:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 backdrop-blur border border-white/40 text-white items-center justify-center hover:bg-white/40 transition-colors" data-testid="hero-prev" aria-label="Previous"><ArrowLeft className="w-4 h-4" /></button>
        <button onClick={() => go(1)} className="hidden sm:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 backdrop-blur border border-white/40 text-white items-center justify-center hover:bg-white/40 transition-colors" data-testid="hero-next" aria-label="Next"><ArrowRight className="w-4 h-4" /></button>
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? "w-8 bg-white" : "w-2.5 bg-white/50"}`} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="bc-ivory border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          <Feature icon={ShieldCheck} title="Perfect Coverage" sub="Designed to fit & flatter" delay={0} />
          <Feature icon={Sparkles} title="Perfectly Soft" sub="Skin-friendly fabrics" delay={100} />
          <Feature icon={Truck} title="COD & WhatsApp" sub="Order your way" delay={200} />
          <Feature icon={MapPin} title="Made in India" sub="AVSK Apparels" delay={300} />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 lg:py-28">
        <Heading eyebrow="Curated for you" title="Shop by Category" />
        <div className="grid md:grid-cols-3 gap-5 mt-14">
          <CategoryTile to="/shop?category=Panties" title="Comfort Panties" img="/collage/Comfort-Panties.png" delay={0} />
          <CategoryTile to="/shop?category=Camisoles" title="Smart Camisoles" img="/collage/Smart-Camisole.png" delay={150} />
          <CategoryTile to="/shop?audience=Kids" title="For Kids" img="/collage/Playful-Kids.png" delay={300} />
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="bc-ivory">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <Heading eyebrow="Just In" title="New Arrivals" sub="Browse our latest comfort essentials" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 mt-14">
            {arrivals.map((p, i) => (
              <div key={p.id} data-aos="fade-up" data-aos-delay={(i % 4) * 120}><ProductCard p={p} /></div>
            ))}
          </div>
          <div className="text-center mt-14" data-aos="fade-up">
            <Link to="/shop" className="inline-block border border-foreground px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-foreground hover:text-[hsl(var(--ivory))] transition-colors duration-300" data-testid="view-all-btn">View All Products</Link>
          </div>
        </div>
      </section>

      {/* PROMO BANNER */}
      <section className="bc-dusty">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 text-center" data-aos="fade-up">
          <p className="text-xs uppercase tracking-luxe text-foreground/70">Special Offer</p>
          <h2 className="font-display text-4xl sm:text-5xl font-light mt-4 text-foreground">
            Get <span className="italic text-primary">20% off</span> your first order
          </h2>
          <p className="text-foreground/70 mt-4 max-w-md mx-auto font-light">Sign up and never miss soft new arrivals and offers made just for you.</p>
          <Link to="/login" className="inline-block mt-8 bg-foreground text-[hsl(var(--ivory))] px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors duration-300" data-testid="promo-register-btn">Register Now</Link>
        </div>
      </section>

      {/* BEST SELLERS */}
      {bestsellers.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <Heading eyebrow="Loved Most" title="Best Sellers" sub="Elevate your everyday with our favourites" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 mt-14">
            {bestsellers.map((p, i) => (
              <div key={p.id} data-aos="fade-up" data-aos-delay={(i % 4) * 120}><ProductCard p={p} /></div>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
      <section className="bc-ivory border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-20 lg:py-28">
          <Heading eyebrow="Kind Words" title="What our customers say" />
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {testimonials.map((t, i) => (
              <figure key={i} className="text-center px-4" data-aos="fade-up" data-aos-delay={i * 150}>
                <div className="text-primary text-3xl font-display leading-none">“</div>
                <blockquote className="font-display text-xl font-light italic leading-relaxed mt-3">{t.quote}</blockquote>
                <figcaption className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-5">— {t.author}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

const Feature = ({ icon: Icon, title, sub, delay }) => (
  <div className="flex items-center gap-3 justify-center lg:justify-start" data-aos="fade-up" data-aos-delay={delay}>
    <Icon className="w-6 h-6 text-primary shrink-0" strokeWidth={1.4} />
    <div>
      <p className="text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  </div>
);

const Heading = ({ eyebrow, title, sub }) => (
  <div className="text-center" data-aos="fade-up">
    <p className="text-xs uppercase tracking-luxe text-primary">{eyebrow}</p>
    <h2 className="font-display text-4xl sm:text-5xl font-light mt-3">{title}</h2>
    {sub && <p className="text-muted-foreground font-light mt-3">{sub}</p>}
  </div>
);

const CategoryTile = ({ to, title, img, delay }) => (
  <Link to={to} className="relative overflow-hidden group block" data-testid={`cat-tile-${title}`} data-aos="fade-up" data-aos-delay={delay}>
    <div className="aspect-[3/5] overflow-hidden">
      <img src={img} alt={title} className="w-full h-full object-cover bc-img-zoom" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
    <div className="absolute bottom-0 inset-x-0 p-7 text-center">
      <h3 className="font-display text-2xl text-white font-light">{title}</h3>
      <span className="inline-flex items-center gap-2 text-white/90 text-[11px] uppercase tracking-[0.2em] mt-2 bc-underline">Explore <ArrowRight className="w-3.5 h-3.5" /></span>
    </div>
  </Link>
);
